import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthAcceptTerms, CompanyDTO, CreateUserNewDTO } from 'src/app/core/models/auth';
import { AuthService } from 'src/app/core/services/shared-services/auth.service';
import { NotificationService } from 'src/app/core/services/shared-services/notification.service';
import { UserRegistrationService } from 'src/app/core/services/user-registration.service';
import { MessageType, PageTitle } from 'src/app/shared/app.constants';
import { TermsType } from 'src/app/shared/app.enums';

@Component({
  selector: 'eb-setup-your-account-new',
  templateUrl: './setup-your-account-new.component.html',
})
export class SetupYourAccountNewComponent implements OnInit {
  @ViewChild('errorContainer', { static: true, read: ViewContainerRef }) errorContainer: ViewContainerRef;
  setUpYourAccountForm!: FormGroup;
  userName: string;
  emailAddress: string;
  idMember: number;
  onCheckMarketingCommunication = false;
  tpaCode: string;
  backRoute: string;
  isEmailUpdateAllowed = false;
  tpaCompanyId: string;
  companyData: CompanyDTO;
  code: string;
  isError: boolean;
  errorMessage: string;
  encryptedIdMember: string;
  hasedIdMember: string;
  expiryTime: string;
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private userRegistrationService: UserRegistrationService,
    private notificationService: NotificationService,
    private titleService: Title,
    private route: ActivatedRoute
  ) {
    this.titleService.setTitle(PageTitle.Registration);
  }

  ngOnInit() {
    this.notificationService.targetElement = this.errorContainer;
    this.setUpYourAccountForm = this.fb.group({
      email: new FormControl('', [Validators.required, Validators.pattern('^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$')]),
      confirmEmail: new FormControl('', [Validators.required, Validators.pattern('^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$')]),

      userName: new FormControl('', [Validators.required, Validators.minLength(8)]),

      password: new FormControl('', [Validators.required]),
      confirmPassword: new FormControl('', [Validators.required]),
    });
    this.getUrlParams();
  }
  confirmEmail() {
    this.authService
      .confirmEmail_v2({
        validate: this.encryptedIdMember,
        jnk: this.hasedIdMember,
        all: this.expiryTime,
      })
      .subscribe(
        (result: any) => {
          if (result) {
            this.userName = result?.username;
            this.emailAddress = result?.email;
            this.tpaCompanyId = result?.tpaCompanyId;
            this.idMember = result?.idMember;

            this.getCompanyData();
            this.isError = false;
            this.errorMessage = '';
          }
          else {
            if (result.errors && result.errors.length > 0) {
              this.errorMessage = result.errors[0].description;
              this.isError = true;
            }
          }
        },
        error => {
          if (error.error && typeof error.error === 'object') {
            if (Array.isArray(error.error)) {
              this.errorMessage = error?.error[0]?.ruleMessage;
            }
          } else if (error.error && typeof error.error === 'string') {
            this.errorMessage = error?.error;
          }
          this.isError = true;
        }
      );
  }

  getUrlParams() {
    this.route.queryParams.subscribe(params => {
      this.encryptedIdMember = params['validate'];
      this.hasedIdMember = params['jnk'];
      this.expiryTime = params['all'];
      this.confirmEmail();
    });
  }

  getCompanyData() {
    if (this.tpaCompanyId) {
      this.authService.validateCompany(this.tpaCompanyId).subscribe((response: CompanyDTO) => {
        if (response) {
          this.companyData = response;
          if (this.companyData) {
            this.backRoute = this.companyData?.tpaCode === 'NYS' ? '/nys-tell-us-about-you' : '/tell-us-about-you';
          }
          this.setRegistrationForm();
        }
      });
    }
  }

  setRegistrationForm() {
    let personalInfo = this.userRegistrationService.getPersonalInfo();

    let confirmEmailAddress =
      this.companyData?.tpaCode === 'NYS'
        ? this.isInvalidEmail(personalInfo?.email)
          ? ''
          : personalInfo?.email
        : this.isInvalidEmail(this.emailAddress)
          ? ''
          : this.emailAddress;
    this.setUpYourAccountForm.patchValue({ confirmEmail: confirmEmailAddress });

    if (this.companyData?.pptIsEmailAsUserName) {
      this.setUpYourAccountForm.removeControl('userName');

      let confirmEmailAddress =
        this.companyData?.tpaCode === 'NYS'
          ? this.isInvalidEmail(personalInfo?.email)
            ? ''
            : personalInfo?.email
          : this.isInvalidEmail(this.emailAddress)
            ? ''
            : this.emailAddress;
      this.setUpYourAccountForm.patchValue({ email: confirmEmailAddress });
    } else {
      this.setUpYourAccountForm.removeControl('email');
      if (this.userName) {
        this.setUpYourAccountForm.patchValue({ userName: this.userName });
      }
      if (this.tpaCompanyId.toLowerCase() !== 'google') {
        this.setUpYourAccountForm.patchValue({ userName: '' });
      }

      if (this.companyData.pptIsAllowEditCredential) {
        this.setUpYourAccountForm.controls['userName'].enable();
      } else {
        this.setUpYourAccountForm.controls['userName'].disable();
      }
      this.setUpYourAccountForm.get('userName').updateValueAndValidity();
    }

    // For enabling, disabling and update the email address.
    let emailAddress = this.companyData?.tpaCode === 'NYS' ? personalInfo?.email : this.emailAddress;
    if (this.companyData?.pptIsAllowEditEmail && (this.isInvalidEmail(emailAddress) || !emailAddress)) {
      this.isEmailUpdateAllowed = true;
      this.setUpYourAccountForm.controls['confirmEmail']?.enable();
    } else {
      this.isEmailUpdateAllowed = false;
      this.setUpYourAccountForm.controls['confirmEmail']?.disable();
    }
  }

  isInvalidEmail(email: string): boolean {
    const invalidEmail = [
      '@noemail.com',
      '@noemail7.com',
      '@noname.com',
      'email@email.com',
      'email@noemail7.com',
      'noname@noname.com',
    ];
    return !email || invalidEmail.some(domain => email.endsWith(domain));
  }

  isPasswordValid(type: string | string[]): boolean {
    const password = this.setUpYourAccountForm.get('password')?.value;
    if (Array.isArray(type)) {
      return type.every(t => this.isPasswordValid(t));
    }
    switch (type) {
      case 'length':
        return password.length >= 8;
      case 'uppercase':
        return /[A-Z]/.test(password);
      case 'lowercase':
        return /[a-z]/.test(password);
      case 'number':
        return /\d/.test(password);
      case 'special':
        return /[!@#$%^&*()_+{}\[\]:;<>,.?~\\-]/.test(password);
      default:
        return false;
    }
  }

  onNext() {
    const createUserPayload: CreateUserNewDTO = {
      username: this.companyData?.pptIsEmailAsUserName
        ? this.setUpYourAccountForm.get('confirmEmail')?.value
        : this.setUpYourAccountForm.get('userName')?.value,
      password: this.setUpYourAccountForm.get('password')?.value,
      confirmPassword: this.setUpYourAccountForm.get('confirmPassword')?.value,
      idMember: this.idMember,
      tpaCode: this.companyData?.tpaCode,
    };
    let acceptTerms: AuthAcceptTerms = {};
    acceptTerms.idMember = this.idMember;
    acceptTerms.termType = TermsType.PPandTOFU;
    acceptTerms.isMarketingCommunicationsAccepted = this.onCheckMarketingCommunication;

    this.authService.createUserWithOutEmailVerification(createUserPayload).subscribe((result: any) => {
      if (result.succeeded) {
        this.authService.acceptTerms(acceptTerms).subscribe(res => {
          if (res) {
            this.router.navigateByUrl('/profile-setup-v2');
          }
        });
      } else if (result.errors && result.errors[0].code === 'UserExists') {
        this.notificationService.displayMessage(
          'This user has already been registered. Please try again or contact our helpful Customer Service team at 888-235-9223.',
          MessageType.Error
        );
      }
    });
  }

  onCheckBox($event: any): void {
    this.onCheckMarketingCommunication = $event.target.checked;
  }
}
